import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ExternalLink, ChevronDown, Calendar as Cal, CheckCircle2, Circle,
  FileText, ListChecks, ChevronRight, Mic2, Search, Gift, UserRound, Save, X,
  Sparkles, Check, RefreshCw, Trash2, UsersRound, Loader2, Undo2, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Markdown from "@/components/Markdown";

const TIME_FMT = { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, TIME_FMT); } catch { return iso; }
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
function fmtBirthday(s) {
  if (!s) return "";
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return `${MONTHS[+m[2] - 1]} ${+m[3]}, ${m[1]}`;
  m = /^(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return `${MONTHS[+m[1] - 1]} ${+m[2]}`;
  return s;
}

function Badge({ children, tone = "teal", testid }) {
  const colors = tone === "purple"
    ? { bg: "rgba(123,111,232,0.12)", bd: "rgba(123,111,232,0.40)", fg: "var(--ph-accent-purple)" }
    : { bg: "rgba(78,205,196,0.12)", bd: "rgba(78,205,196,0.42)", fg: "var(--ph-product-teal)" };
  return (
    <span data-testid={testid} style={{
      fontSize: 10, letterSpacing: "0.08em", padding: "2px 8px", borderRadius: 999,
      background: colors.bg, border: `1px solid ${colors.bd}`, color: colors.fg,
      fontWeight: 600, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

export default function People() {
  const { user } = useAuth();
  const autoLearn = user?.auto_learn_enabled !== false;
  const [people, setPeople] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [query, setQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState("All");
  const [suggestions, setSuggestions] = useState([]);      // manual mode: pending
  const [learned, setLearned] = useState([]);              // auto mode: recently learned
  const [pendingCount, setPendingCount] = useState(0);     // backlog banner
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);
  const [scanLoading, setScanLoading] = useState(false);
  const [rejudging, setRejudging] = useState(false);
  const navigate = useNavigate();

  const loadBirthdays = () =>
    api.get("/people/birthdays?days=30").then((r) => setBirthdays(r.data.upcoming || [])).catch(() => {});

  const loadSuggestions = () =>
    api.get("/people/suggestions").then((r) => setSuggestions(r.data.suggestions || [])).catch(() => {});

  const loadLearned = () =>
    api.get("/people/suggestions?status=auto_applied").then((r) => setLearned(r.data.suggestions || [])).catch(() => {});

  const loadPendingCount = () =>
    api.get("/people/suggestions/pending-count").then((r) => setPendingCount(r.data.pending || 0)).catch(() => {});

  const refreshPeople = () => api.get("/people").then((r) => setPeople(r.data)).catch(() => {});

  const triggerScan = async () => {
    if (scanLoading) return;
    setScanLoading(true);
    try {
      const r = await api.post("/people/suggestions/scan");
      toast.success(r.data?.message || "Scanning your recent meetings.");
      // Poll a few times so the UI fills in as Claude completes each meeting.
      for (let i = 0; i < 5; i += 1) {
        await new Promise((res) => setTimeout(res, 15000));
        if (autoLearn) { await loadLearned(); await refreshPeople(); }
        else await loadSuggestions();
      }
    } catch {
      toast.error("Couldn't start the scan.");
    } finally {
      setScanLoading(false);
    }
  };

  const triggerRejudge = async () => {
    if (rejudging) return;
    setRejudging(true);
    try {
      const r = await api.post("/people/suggestions/rejudge");
      toast.success(r.data?.message || "Andre is sorting your older suggestions.");
      // Poll while Andre classifies + applies in the background.
      for (let i = 0; i < 6; i += 1) {
        await new Promise((res) => setTimeout(res, 12000));
        await Promise.all([loadPendingCount(), loadLearned(), refreshPeople(), loadBirthdays()]);
      }
    } catch {
      toast.error("Couldn't start the cleanup.");
    } finally {
      setRejudging(false);
    }
  };

  const undoLearned = async (s) => {
    try {
      await api.post(`/people/suggestions/${s.id}/undo`);
      toast.success(`Undone — removed from ${s.person_name.split(" ")[0]}.`);
      setLearned((cur) => cur.filter((x) => x.id !== s.id));
      refreshPeople();
      loadBirthdays();
    } catch {
      toast.error("Couldn't undo that.");
    }
  };

  const approveSuggestion = async (s, editedValue = null) => {
    try {
      await api.post(`/people/suggestions/${s.id}/approve`,
        editedValue ? { suggested_value: editedValue } : {});
      toast.success(`Added to ${s.person_name}.`);
      setSuggestions((cur) => cur.filter((x) => x.id !== s.id));
      refreshPeople();
      loadBirthdays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply suggestion.");
    }
  };

  const dismissSuggestion = async (s) => {
    try {
      await api.post(`/people/suggestions/${s.id}/dismiss`);
      setSuggestions((cur) => cur.filter((x) => x.id !== s.id));
    } catch {
      toast.error("Couldn't dismiss.");
    }
  };

  useEffect(() => {
    refreshPeople();
    loadBirthdays();
    loadPendingCount();
    if (autoLearn) loadLearned();
    else loadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLearn]);

  const groups = useMemo(() => {
    const s = new Set();
    people.forEach((p) => (p.groups || []).forEach((g) => s.add(g)));
    return Array.from(s).sort();
  }, [people]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people.filter((p) => {
      if (activeGroup !== "All" && !(p.groups || []).includes(activeGroup)) return false;
      if (!q) return true;
      const hay = [p.name, p.role, p.relationship, ...(p.groups || [])].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [people, query, activeGroup]);

  const onUpdated = (up, meta) => {
    // Delete: drop from list and collapse the detail.
    if (meta?.deleted) {
      setPeople((cur) => cur.filter((p) => p.id !== meta.deleted));
      if (expandedId === meta.deleted) setExpandedId(null);
      loadBirthdays();
      return;
    }
    // Merge: drop the source (mergedFrom), patch the target, expand the target.
    if (meta?.mergedFrom && up) {
      setPeople((cur) => {
        const next = cur
          .filter((p) => p.id !== meta.mergedFrom)
          .map((p) => (p.id === up.id ? { ...p, ...up } : p));
        return next;
      });
      setExpandedId(up.id);
      loadBirthdays();
      loadSuggestions();
      return;
    }
    if (up) {
      setPeople((cur) => cur.map((p) => (p.id === up.id ? { ...p, ...up } : p)));
      loadBirthdays();
    }
  };

  const toggle = (p) => setExpandedId((cur) => (cur === p.id ? null : p.id));

  return (
    <>
      <div className="surface-header">
        <div className="surface-title">People</div>
        <span className="status-pill"><span className="dot" />Auto-built from your meetings & inbox</span>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "24px clamp(14px, 4vw, 32px) 60px" }} data-testid="people-list">
        <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", flexDirection: "column", gap: 10 }}>

          {birthdays.length > 0 && (
            <div className="panel" data-testid="birthday-strip" style={{ padding: "14px 18px", marginBottom: 8 }}>
              <div style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ph-product-teal)", fontWeight: 600, marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>
                <Gift size={13} /> Upcoming birthdays
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {birthdays.map((b) => (
                  <button
                    key={b.id}
                    data-testid={`birthday-chip-${b.id}`}
                    onClick={() => { setExpandedId(b.id); setActiveGroup("All"); setQuery(""); }}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 12px",
                      borderRadius: 999, background: "var(--panel-2)", border: "1px solid var(--hairline)",
                      color: "var(--ink-soft)", fontSize: 12.5, cursor: "pointer",
                    }}
                  >
                    <strong style={{ color: "var(--ink)" }}>{b.name}</strong>
                    {b.relationship && <span className="text-dim">({b.relationship})</span>}
                    <span>{b.display}</span>
                    <span style={{ color: "var(--ph-product-teal)", fontWeight: 600 }}>
                      {b.days_until === 0 ? "today" : `in ${b.days_until}d`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <SuggestionsPanel
            autoLearn={autoLearn}
            suggestions={suggestions}
            learned={learned}
            pendingCount={pendingCount}
            open={suggestionsOpen}
            onToggle={() => setSuggestionsOpen((v) => !v)}
            onApprove={approveSuggestion}
            onDismiss={dismissSuggestion}
            onUndo={undoLearned}
            onScan={triggerScan}
            onRejudge={triggerRejudge}
            rejudging={rejudging}
            scanLoading={scanLoading}
          />

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-dim)" }} />
              <input
                className="field"
                placeholder="Search people, groups, relationships…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="people-search-input"
                style={{ paddingLeft: 34 }}
              />
            </div>
          </div>

          {groups.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }} data-testid="group-filter-bar">
              {["All", ...groups].map((g) => {
                const active = activeGroup === g;
                const count = g === "All" ? people.length : people.filter((p) => (p.groups || []).includes(g)).length;
                return (
                  <button
                    key={g}
                    onClick={() => setActiveGroup(g)}
                    data-testid={`group-chip-${g.replace(/\s+/g, "-").toLowerCase()}`}
                    style={{
                      padding: "5px 13px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
                      fontWeight: active ? 600 : 500,
                      background: active ? "rgba(78,205,196,0.14)" : "var(--panel)",
                      border: `1px solid ${active ? "rgba(78,205,196,0.5)" : "var(--hairline)"}`,
                      color: active ? "var(--ph-product-teal)" : "var(--ink-soft)",
                    }}
                  >
                    {g} <span style={{ opacity: 0.65 }}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {filtered.length === 0 && <div className="text-dim">No people match.</div>}
          {filtered.map((p) => {
            const isOpen = expandedId === p.id;
            return (
              <div key={p.id} className="fade-up" data-testid={`person-${p.id}`}>
                <div
                  className="card-row"
                  onClick={() => toggle(p)}
                  data-testid={`person-card-${p.id}`}
                  style={{
                    cursor: "pointer",
                    borderColor: isOpen ? "var(--cobalt-bright)" : undefined,
                    borderBottomLeftRadius: isOpen ? 0 : undefined,
                    borderBottomRightRadius: isOpen ? 0 : undefined,
                  }}
                >
                  <div className="avatar-circle" style={{ background: p.color }}>
                    {p.initials || (p.name[0] || "?").toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {p.name}
                      {p.relationship && <Badge testid={`rel-badge-${p.id}`}>{p.relationship}</Badge>}
                      {(p.groups || []).map((g) => <Badge key={g} tone="purple">{g}</Badge>)}
                    </div>
                    <div className="text-dim" style={{ fontSize: 12, marginTop: 2 }}>
                      {p.role}
                      {p.birthday && <span>{p.role ? " · " : ""}Birthday {fmtBirthday(p.birthday)}</span>}
                    </div>
                  </div>
                  <div className="text-soft person-row-notes" data-testid={`person-row-notes-${p.id}`}>
                    {p.notes}
                  </div>
                  <ChevronDown
                    size={16}
                    style={{
                      color: "var(--ink-dim)",
                      transition: "transform 180ms ease",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      flexShrink: 0,
                    }}
                  />
                </div>
                {isOpen && <PersonDetail person={p} navigate={navigate} onUpdated={onUpdated} />}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ---------- Suggestions / learned panel (context from Granola transcripts) ----------
function SuggestionsPanel({
  autoLearn, suggestions, learned, pendingCount, open, onToggle,
  onApprove, onDismiss, onUndo, onScan, onRejudge, rejudging, scanLoading,
}) {
  const [showAll, setShowAll] = useState(false);

  // Manual mode: group pending suggestions by person.
  const groupedPending = useMemo(() => {
    const byPerson = new Map();
    suggestions.forEach((s) => {
      if (!byPerson.has(s.person_id)) byPerson.set(s.person_id, { name: s.person_name, items: [] });
      byPerson.get(s.person_id).items.push(s);
    });
    return Array.from(byPerson.values()).sort((a, b) => b.items.length - a.items.length);
  }, [suggestions]);

  if (autoLearn) {
    const shown = showAll ? learned : learned.slice(0, 8);
    return (
      <div className="panel" data-testid="suggestions-panel" style={{ padding: "14px 18px", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <button
            onClick={onToggle}
            data-testid="suggestions-toggle"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--ph-product-teal)", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}
          >
            <Sparkles size={13} /> Recently learned by {"Andre"}
            {learned.length > 0 && (
              <span style={{ marginLeft: 6, padding: "2px 8px", borderRadius: 999, background: "var(--panel-2)", color: "var(--ink-soft)", fontSize: 11 }}>
                {learned.length}
              </span>
            )}
            <ChevronDown size={13} style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 120ms" }} />
          </button>
          <button className="btn btn-ghost" onClick={onScan} disabled={scanLoading} data-testid="suggestions-scan-btn" style={{ fontSize: 12 }}>
            <RefreshCw size={13} className={scanLoading ? "spin" : ""} />
            {scanLoading ? " Scanning…" : " Scan recent meetings"}
          </button>
        </div>

        {/* Backlog banner — re-judge the old pending pile once. */}
        {pendingCount > 0 && (
          <div data-testid="rejudge-banner" style={{ marginTop: 12, padding: "12px 14px", borderRadius: 10, background: "rgba(123,111,232,0.10)", border: "1px solid rgba(123,111,232,0.35)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Wand2 size={16} style={{ color: "var(--ph-accent-purple)", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 180, fontSize: 13, color: "var(--ink-soft)" }}>
              You have <strong style={{ color: "var(--ink)" }}>{pendingCount}</strong> older suggestion{pendingCount === 1 ? "" : "s"} from before auto-learn. Let Andre keep the durable facts and drop the meeting noise.
            </div>
            <button className="btn btn-primary" onClick={onRejudge} disabled={rejudging} data-testid="rejudge-btn" style={{ fontSize: 12 }}>
              {rejudging ? <Loader2 size={13} className="spin" /> : <Wand2 size={13} />}
              {rejudging ? " Sorting…" : " Auto-sort them"}
            </button>
          </div>
        )}

        {open && learned.length === 0 && pendingCount === 0 && (
          <div style={{ fontSize: 13, color: "var(--ink-dim)", padding: "10px 0 2px" }}>
            As your Granola meetings sync, Andre adds durable facts (roles, family, birthdays, locations)
            straight to each person &mdash; they&apos;ll appear here, and you can undo anything.
          </div>
        )}

        {open && learned.length > 0 && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
              {shown.map((s) => (
                <LearnedRow key={s.id} s={s} onUndo={onUndo} />
              ))}
            </div>
            {learned.length > 8 && (
              <button onClick={() => setShowAll((v) => !v)} data-testid="learned-show-all" style={{ marginTop: 10, background: "none", border: "none", color: "var(--ph-product-teal)", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                {showAll ? "Show less" : `Show all ${learned.length}`}
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  // ----- Manual review mode (auto-learn OFF) -----
  const shownPending = showAll ? groupedPending : groupedPending.slice(0, 4);
  const count = suggestions.length;
  return (
    <div className="panel" data-testid="suggestions-panel" style={{ padding: "14px 18px", marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: count > 0 ? 12 : 0 }}>
        <button
          onClick={onToggle}
          data-testid="suggestions-toggle"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--ph-accent-purple, #7B6FE8)", fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}
        >
          <Sparkles size={13} /> Suggested profile updates
          {count > 0 && (
            <span style={{ marginLeft: 6, padding: "2px 8px", borderRadius: 999, background: "var(--panel-2)", color: "var(--ink-soft)", fontSize: 11, letterSpacing: "0.04em" }}>
              {count}
            </span>
          )}
          <ChevronDown size={13} style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 120ms" }} />
        </button>
        <button className="btn btn-ghost" onClick={onScan} disabled={scanLoading} data-testid="suggestions-scan-btn" style={{ fontSize: 12 }}>
          <RefreshCw size={13} className={scanLoading ? "spin" : ""} />
          {scanLoading ? " Scanning…" : " Scan recent meetings"}
        </button>
      </div>

      {open && count === 0 && (
        <div style={{ fontSize: 13, color: "var(--ink-dim)", padding: "4px 0" }}>
          Nothing to suggest yet. Andre will mine new Granola meetings for profile facts
          (birthdays, family, locations, role changes) &mdash; review them here before they&apos;re saved.
          Turn on <strong>Auto-learn</strong> in Settings to skip the review step entirely.
        </div>
      )}

      {open && count > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {shownPending.map((g) => (
              <SuggestionPersonGroup key={g.name} personName={g.name} items={g.items} onApprove={onApprove} onDismiss={onDismiss} />
            ))}
          </div>
          {groupedPending.length > 4 && (
            <button onClick={() => setShowAll((v) => !v)} data-testid="suggestions-show-all" style={{ marginTop: 10, background: "none", border: "none", color: "var(--ph-product-teal)", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
              {showAll ? "Show less" : `Show all ${groupedPending.length} people`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

const FIELD_LABELS = {
  relationship: "Relationship", groups: "Groups", birthday: "Birthday",
  location: "Location", address: "Address", email: "Email", phone: "Phone",
  notes: "Notes", role: "Role",
};

function LearnedRow({ s, onUndo }) {
  const label = FIELD_LABELS[s.field] || s.field;
  return (
    <div data-testid={`learned-${s.id}`} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--panel-2)", border: "1px solid var(--hairline)", display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: "var(--ink)", marginBottom: 3 }}>
          <strong>{s.person_name}</strong>
          <span className="props-chip" style={{ fontSize: 10, marginLeft: 8 }}>{label}</span>
        </div>
        <div style={{ fontSize: 13.5, color: "var(--ink-soft)", whiteSpace: "pre-wrap" }}>
          {s.applied_value || s.suggested_value}
        </div>
        {s.source_meeting_title && (
          <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 4 }}>
            from <span style={{ color: "var(--ink-soft)" }}>{s.source_meeting_title}</span>
          </div>
        )}
      </div>
      <button className="btn btn-ghost" onClick={() => onUndo(s)} data-testid={`learned-undo-${s.id}`} style={{ fontSize: 12, flexShrink: 0 }}>
        <Undo2 size={12} /> Undo
      </button>
    </div>
  );
}

function SuggestionPersonGroup({ personName, items, onApprove, onDismiss }) {
  return (
    <div data-testid={`suggestion-group-${personName}`} style={{ borderTop: "1px solid var(--hairline)", paddingTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>
        {personName}
        <span className="text-dim" style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 400 }}>
          {items.length} suggestion{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((s) => (
          <SuggestionRow key={s.id} s={s} onApprove={onApprove} onDismiss={onDismiss} />
        ))}
      </div>
    </div>
  );
}

function SuggestionRow({ s, onApprove, onDismiss }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(s.suggested_value);
  const fieldLabel = {
    relationship: "Relationship", groups: "Groups", birthday: "Birthday",
    location: "Location", address: "Address", email: "Email", phone: "Phone",
    notes: "Notes", role: "Role",
  }[s.field] || s.field;
  return (
    <div data-testid={`suggestion-${s.id}`} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--panel-2)", border: "1px solid var(--hairline)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "var(--ink-dim)", marginBottom: 6 }}>
        <span className="props-chip" style={{ fontSize: 10.5 }}>{fieldLabel}</span>
        {s.current_value && (
          <span>was: <span style={{ color: "var(--ink-soft)" }}>{s.current_value.length > 80 ? s.current_value.slice(0, 80) + "…" : s.current_value}</span></span>
        )}
      </div>
      {editing ? (
        <textarea
          className="field"
          rows={s.field === "notes" ? 3 : 1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid={`suggestion-edit-${s.id}`}
          style={{ width: "100%", marginBottom: 6, fontSize: 13 }}
        />
      ) : (
        <div style={{ fontSize: 14, color: "var(--ink)", marginBottom: 6, whiteSpace: "pre-wrap" }}>
          {s.suggested_value}
        </div>
      )}
      {s.evidence && (
        <div style={{ fontSize: 11.5, color: "var(--ink-dim)", fontStyle: "italic", marginBottom: 6, paddingLeft: 10, borderLeft: "2px solid var(--hairline)" }}>
          “{s.evidence.length > 180 ? s.evidence.slice(0, 180) + "…" : s.evidence}”
        </div>
      )}
      <div style={{ fontSize: 11, color: "var(--ink-dim)", marginBottom: 8 }}>
        from <span style={{ color: "var(--ink-soft)" }}>{s.source_meeting_title || "—"}</span>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          className="btn btn-ghost"
          onClick={() => onDismiss(s)}
          data-testid={`suggestion-dismiss-${s.id}`}
          style={{ fontSize: 12 }}
        >
          <X size={12} /> Dismiss
        </button>
        {editing ? (
          <>
            <button className="btn btn-ghost" onClick={() => { setEditing(false); setValue(s.suggested_value); }} style={{ fontSize: 12 }}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={() => onApprove(s, value)}
              data-testid={`suggestion-save-${s.id}`}
              style={{ fontSize: 12 }}
            >
              <Save size={12} /> Save edits
            </button>
          </>
        ) : (
          <>
            <button
              className="btn"
              onClick={() => setEditing(true)}
              data-testid={`suggestion-edit-btn-${s.id}`}
              style={{ fontSize: 12 }}
            >
              Edit
            </button>
            <button
              className="btn btn-primary"
              onClick={() => onApprove(s)}
              data-testid={`suggestion-approve-${s.id}`}
              style={{ fontSize: 12 }}
            >
              <Check size={12} /> Add to {s.person_name.split(" ")[0]}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function PersonDetail({ person, navigate, onUpdated }) {
  const [tab, setTab] = useState("profile");
  const [detail, setDetail] = useState(null); // { person, meetings, action_items }
  const [vaultDoc, setVaultDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDetail = async () => {
    try {
      const r = await api.get(`/people/${person.id}/meetings`);
      setDetail(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get(`/people/${person.id}/meetings`).then((r) => { if (mounted) setDetail(r.data); }).catch(() => {}),
      person.vault_doc_id
        ? api.get(`/vault/docs/${person.vault_doc_id}`).then((r) => { if (mounted) setVaultDoc(r.data); }).catch(() => {})
        : Promise.resolve(),
    ]).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [person.id, person.vault_doc_id]);

  const meetings = detail?.meetings || [];
  const openActions = [];
  meetings.forEach((m) => {
    (m.action_items || []).forEach((a, idx) => {
      if (a && !a.done) openActions.push({ ...a, idx, meeting_id: m.id, meeting_title: m.title, date: m.date });
    });
  });

  const toggleAction = async (meetingId, idx) => {
    try {
      await api.patch(`/meetings/${meetingId}/action/${idx}`);
      await loadDetail();
    } catch { /* ignore */ }
  };

  return (
    <div
      data-testid={`person-detail-${person.id}`}
      className="person-detail"
    >
      {/* Tabs */}
      <div
        className="person-tabs"
        data-testid={`person-tabs-${person.id}`}
      >
        <DetailTab Icon={UserRound} label="Profile" active={tab === "profile"} onClick={() => setTab("profile")} testid={`person-tab-profile-${person.id}`} />
        <DetailTab Icon={FileText} label="Notes" active={tab === "notes"} onClick={() => setTab("notes")} testid={`person-tab-notes-${person.id}`} />
        <DetailTab Icon={Cal} label="Meetings" count={meetings.length} active={tab === "meetings"} onClick={() => setTab("meetings")} testid={`person-tab-meetings-${person.id}`} />
        <DetailTab Icon={ListChecks} label="Action items" count={openActions.length} active={tab === "actions"} onClick={() => setTab("actions")} testid={`person-tab-actions-${person.id}`} />
      </div>

      {tab === "profile" && (
        <ProfileTab key={person.id} person={person} onUpdated={onUpdated} />
      )}

      {tab === "notes" && (
        <NotesTab person={person} vaultDoc={vaultDoc} navigate={navigate} />
      )}

      {tab === "meetings" && (
        <MeetingsTab meetings={meetings} loading={loading} onToggleAction={toggleAction} navigate={navigate} pid={person.id} />
      )}

      {tab === "actions" && (
        <ActionsTab actions={openActions} loading={loading} onToggle={toggleAction} navigate={navigate} pid={person.id} />
      )}
    </div>
  );
}

function DetailTab({ Icon, label, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        background: "transparent", border: "none", padding: "8px 2px 10px", cursor: "pointer",
        fontSize: 13.5, display: "inline-flex", alignItems: "center", gap: 7,
        color: active ? "var(--ink)" : "var(--ink-dim)",
        borderBottom: active ? "2px solid var(--ph-product-teal)" : "2px solid transparent",
        fontWeight: active ? 600 : 500, marginBottom: -1,
      }}
    >
      <Icon size={14} strokeWidth={2} />
      {label}
      {typeof count === "number" && count > 0 && (
        <span style={{
          fontSize: 10.5, fontWeight: 600, padding: "1px 7px", borderRadius: 999,
          background: "rgba(78,205,196,0.14)", border: "1px solid rgba(78,205,196,0.40)",
          color: "var(--ph-product-teal)",
        }}>{count}</span>
      )}
    </button>
  );
}

function NotesTab({ person, vaultDoc, navigate }) {
  if (vaultDoc) {
    return (
      <div data-testid={`person-notes-${person.id}`}>
        <Markdown text={vaultDoc.markdown} />
        <div style={{ marginTop: 16 }}>
          <button
            className="btn"
            onClick={(e) => { e.stopPropagation(); navigate(`/vault/${vaultDoc.id}`); }}
            data-testid={`open-in-vault-${person.id}`}
          >
            <ExternalLink size={14} /> Open in Vault
          </button>
        </div>
      </div>
    );
  }
  if (person.vault_doc_id) {
    return <div className="text-dim" style={{ fontSize: 13 }}>Opening notes…</div>;
  }
  return (
    <div className="text-dim" style={{ fontSize: 13 }}>
      No vault note for {person.name.split(" ")[0]} yet — they were inferred from your inbox.
    </div>
  );
}

function MeetingsTab({ meetings, loading, onToggleAction, navigate, pid }) {
  const [openId, setOpenId] = useState(null);
  if (loading) return <div className="text-dim" style={{ fontSize: 13 }}>Loading meetings…</div>;
  if (meetings.length === 0) {
    return <div className="text-dim" style={{ fontSize: 13 }} data-testid={`person-meetings-empty-${pid}`}>No meetings logged with this person yet.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid={`person-meetings-${pid}`}>
      {meetings.map((m) => {
        const isOpen = openId === m.id;
        const openCount = (m.action_items || []).filter((a) => !a.done).length;
        return (
          <div
            key={m.id}
            data-testid={`person-meeting-${m.id}`}
            style={{
              background: "var(--panel)", border: "1px solid var(--hairline)",
              borderLeft: `3px solid ${m.color || "#6B5CE7"}`, borderRadius: 10, overflow: "hidden",
            }}
          >
            <div
              onClick={() => setOpenId(isOpen ? null : m.id)}
              data-testid={`person-meeting-row-${m.id}`}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer" }}
            >
              <ChevronRight
                size={15}
                style={{ color: "var(--ink-dim)", flexShrink: 0, transition: "transform 160ms ease", transform: isOpen ? "rotate(90deg)" : "none" }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink)", fontWeight: 600 }}>
                  {m.title}
                </div>
                <div className="text-dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                  {fmtDate(m.date)}{m.attendees?.length ? ` · ${m.attendees.join(", ")}` : ""}
                </div>
              </div>
              {openCount > 0 && (
                <span style={{
                  color: "var(--ph-accent-purple)", fontWeight: 600, padding: "2px 8px", borderRadius: 999,
                  background: "rgba(123,111,232,0.12)", border: "1px solid rgba(123,111,232,0.40)",
                  fontSize: 10, letterSpacing: "0.08em", flexShrink: 0,
                }}>{openCount} OPEN</span>
              )}
            </div>

            {isOpen && (
              <div style={{ padding: "2px 16px 16px 41px", display: "flex", flexDirection: "column", gap: 14 }} data-testid={`person-meeting-detail-${m.id}`}>
                {m.tldr && (
                  <div>
                    <SubLabel>TL;DR</SubLabel>
                    <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.65 }}>{m.tldr}</div>
                  </div>
                )}

                {(m.action_items || []).length > 0 && (
                  <div>
                    <SubLabel>Action items</SubLabel>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(m.action_items || []).map((a, idx) => (
                        <div
                          key={idx}
                          onClick={() => onToggleAction(m.id, idx)}
                          data-testid={`person-meeting-action-${m.id}-${idx}`}
                          style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "8px 10px", borderRadius: 8, background: "var(--panel-2)", border: "1px solid var(--hairline)", cursor: "pointer" }}
                        >
                          {a.done
                            ? <CheckCircle2 size={15} style={{ color: "var(--good)", marginTop: 1, flexShrink: 0 }} />
                            : <Circle size={15} style={{ color: "var(--ink-dim)", marginTop: 1, flexShrink: 0 }} />}
                          <div style={{ fontSize: 13, color: a.done ? "var(--ink-dim)" : "var(--ink)", textDecoration: a.done ? "line-through" : "none" }}>
                            {a.owner && <strong>{a.owner}</strong>}{a.owner ? " — " : ""}{a.text}
                            {a.due && <span className="text-dim" style={{ marginLeft: 6 }}>· due {a.due}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {m.transcript && <TranscriptToggle transcript={m.transcript} mid={m.id} />}

                <div>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 12.5 }}
                    onClick={(e) => { e.stopPropagation(); navigate(`/meetings/${m.id}`); }}
                    data-testid={`person-open-meeting-${m.id}`}
                  >
                    <ExternalLink size={13} /> Open full meeting
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TranscriptToggle({ transcript, mid }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <button
        className="btn btn-ghost"
        style={{ fontSize: 12, padding: "4px 8px" }}
        onClick={(e) => { e.stopPropagation(); setShow((s) => !s); }}
        data-testid={`person-transcript-toggle-${mid}`}
      >
        <Mic2 size={12} /> {show ? "Hide transcript" : "Show transcript"}
      </button>
      {show && (
        <pre
          data-testid={`person-transcript-${mid}`}
          style={{
            fontFamily: "inherit", whiteSpace: "pre-wrap", fontSize: 12.5, lineHeight: 1.6,
            color: "var(--ink-soft)", padding: 14, borderRadius: 8, marginTop: 8,
            background: "var(--panel-2)", border: "1px solid var(--hairline)",
          }}
        >
          {transcript}
        </pre>
      )}
    </div>
  );
}

function ActionsTab({ actions, loading, onToggle, navigate, pid }) {
  if (loading) return <div className="text-dim" style={{ fontSize: 13 }}>Loading…</div>;
  if (actions.length === 0) {
    return <div className="text-dim" style={{ fontSize: 13 }} data-testid={`person-actions-empty-${pid}`}>No open action items for this person. Nice.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid={`person-actions-${pid}`}>
      {actions.map((a) => (
        <div
          key={`${a.meeting_id}-${a.idx}`}
          data-testid={`person-action-${a.meeting_id}-${a.idx}`}
          style={{ display: "flex", gap: 11, alignItems: "flex-start", padding: "12px 14px", background: "var(--panel)", border: "1px solid var(--hairline)", borderLeft: "3px solid var(--ph-accent-purple)", borderRadius: 10 }}
        >
          <button
            onClick={() => onToggle(a.meeting_id, a.idx)}
            data-testid={`person-action-toggle-${a.meeting_id}-${a.idx}`}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, marginTop: 1, flexShrink: 0 }}
            title="Mark done"
          >
            <Circle size={16} style={{ color: "var(--ink-dim)" }} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, color: "var(--ink)" }}>
              {a.owner && <strong>{a.owner}</strong>}{a.owner ? " — " : ""}{a.text}
            </div>
            <div className="text-dim" style={{ fontSize: 11.5, marginTop: 4, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span
                onClick={() => navigate(`/meetings/${a.meeting_id}`)}
                style={{ cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: "var(--hairline)" }}
              >
                {a.meeting_title}
              </span>
              <span>· {fmtDate(a.date)}</span>
              {a.due && <span>· due {a.due}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SubLabel({ children }) {
  return (
    <div style={{ fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ph-accent-purple)", fontWeight: 600, marginBottom: 8 }}>
      {children}
    </div>
  );
}

function ProfileTab({ person, onUpdated }) {
  const [form, setForm] = useState({
    role: person.role || "",
    relationship: person.relationship || "",
    groups: person.groups || [],
    birthday: person.birthday || "",
    location: person.location || "",
    address: person.address || "",
    email: person.email || "",
    phone: person.phone || "",
    notes: person.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (form.birthday && !/^(\d{4}-)?\d{1,2}-\d{1,2}$/.test(form.birthday.trim())) {
      toast.error("Birthday should be YYYY-MM-DD or MM-DD (e.g. 1985-03-14 or 03-14).");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/people/${person.id}`, {
        ...form,
        birthday: form.birthday.trim() || null,
      });
      onUpdated?.(r.data);
      toast.success(`${person.name.split(" ")[0]}'s profile saved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`person-profile-${person.id}`} className="profile-form">
      {/* Identity section */}
      <section className="profile-section">
        <h4 className="profile-section-title">Identity</h4>
        <div className="profile-grid">
          <Field label="Relationship">
            <input className="field" value={form.relationship} onChange={set("relationship")}
              placeholder="Wife, Son, Broker…" data-testid={`profile-relationship-${person.id}`} />
          </Field>
          <Field label="Role / title">
            <input className="field" value={form.role} onChange={set("role")}
              placeholder="Head of Claims" data-testid={`profile-role-${person.id}`} />
          </Field>
          <Field label="Birthday" hint="YYYY-MM-DD or MM-DD">
            <input className="field" value={form.birthday} onChange={set("birthday")}
              placeholder="1985-03-14 or 03-14" data-testid={`profile-birthday-${person.id}`} />
          </Field>
        </div>
      </section>

      {/* Contact section */}
      <section className="profile-section">
        <h4 className="profile-section-title">Contact</h4>
        <div className="profile-grid">
          <Field label="Location">
            <input className="field" value={form.location} onChange={set("location")}
              placeholder="Minneapolis, MN" data-testid={`profile-location-${person.id}`} />
          </Field>
          <Field label="Email">
            <input className="field" value={form.email} onChange={set("email")}
              placeholder="name@example.com" data-testid={`profile-email-${person.id}`} />
          </Field>
          <Field label="Phone">
            <input className="field" value={form.phone} onChange={set("phone")}
              placeholder="+1 …" data-testid={`profile-phone-${person.id}`} />
          </Field>
        </div>
        <Field label="Address">
          <input className="field" value={form.address} onChange={set("address")}
            placeholder="Street, city, state, zip" data-testid={`profile-address-${person.id}`} />
        </Field>
      </section>

      {/* Groups + Notes */}
      <section className="profile-section">
        <h4 className="profile-section-title">Tags &amp; notes</h4>
        <Field label="Groups">
          <GroupsInput
            value={form.groups}
            onChange={(groups) => setForm((f) => ({ ...f, groups }))}
            pid={person.id}
          />
        </Field>
        <Field label="Notes" hint="Free-form context Andre uses to know this person — kids' names, anniversary, allergies, gift ideas, how you met, preferences.">
          <textarea
            className="field profile-notes-area"
            value={form.notes}
            onChange={set("notes")}
            rows={Math.max(4, Math.min(14, (form.notes || "").split("\n").length + 1))}
            placeholder="Edit freely — you can rewrite, trim, or erase anything Andre captured that doesn't belong here."
            data-testid={`profile-notes-${person.id}`}
          />
        </Field>
      </section>

      {/* Action bar */}
      <div className="profile-actions">
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid={`profile-save-${person.id}`}>
          <Save size={14} /> {saving ? "Saving…" : "Save profile"}
        </button>
        <div className="profile-actions-spacer" />
        <button
          className="profile-action-secondary"
          onClick={() => setShowMerge(true)}
          data-testid={`profile-merge-btn-${person.id}`}
        >
          <UsersRound size={13} /> Merge into another
        </button>
        <button
          className="profile-action-danger"
          onClick={() => setShowDelete(true)}
          data-testid={`profile-delete-btn-${person.id}`}
        >
          <Trash2 size={13} /> Delete person
        </button>
      </div>

      {showMerge && (
        <MergeDialog
          person={person}
          onClose={() => setShowMerge(false)}
          onMerged={(target) => {
            setShowMerge(false);
            onUpdated?.(target, { mergedFrom: person.id });
          }}
        />
      )}
      {showDelete && (
        <DeleteDialog
          person={person}
          onClose={() => setShowDelete(false)}
          onDeleted={() => {
            setShowDelete(false);
            onUpdated?.(null, { deleted: person.id });
          }}
        />
      )}
    </div>
  );
}

// ---------- Merge dialog ----------
function MergeDialog({ person, onClose, onMerged }) {
  const [people, setPeople] = useState([]);
  const [query, setQuery] = useState("");
  const [targetId, setTargetId] = useState("");
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    api.get("/people").then((r) => {
      setPeople((r.data || []).filter((p) => p.id !== person.id));
    }).catch(() => {});
  }, [person.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people.slice(0, 12);
    return people.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.role || "").toLowerCase().includes(q) ||
      (p.relationship || "").toLowerCase().includes(q)
    ).slice(0, 20);
  }, [people, query]);

  const target = people.find((p) => p.id === targetId);

  const merge = async () => {
    if (!targetId || merging) return;
    setMerging(true);
    try {
      const r = await api.post(`/people/${person.id}/merge`, { target_id: targetId });
      toast.success(`Merged ${person.name.split(" ")[0]} into ${target?.name?.split(" ")[0] || "target"}.`);
      onMerged?.(r.data.target);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't merge.");
    } finally {
      setMerging(false);
    }
  };

  return (
    <DialogShell title={`Merge ${person.name} into another person`} onClose={onClose} testid={`merge-dialog-${person.id}`}>
      <div className="text-dim" style={{ fontSize: 13, marginBottom: 14 }}>
        Use this when Andre captured something under the wrong name (e.g. &quot;Jenny&quot; instead of
        &quot;Jennie Burks&quot;). Notes, groups, and any blank fields from <strong>{person.name}</strong> will
        be folded into the target, then <strong>{person.name}</strong> will be deleted.
      </div>
      <input
        className="field"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by name, role, or relationship…"
        data-testid={`merge-search-${person.id}`}
        style={{ marginBottom: 10 }}
        autoFocus
      />
      <div
        data-testid={`merge-list-${person.id}`}
        style={{ maxHeight: 280, overflowY: "auto", border: "1px solid var(--hairline)", borderRadius: 8, marginBottom: 12 }}
      >
        {filtered.length === 0 ? (
          <div className="text-dim" style={{ fontSize: 13, padding: 16 }}>No matching people.</div>
        ) : (
          filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => setTargetId(p.id)}
              data-testid={`merge-target-${p.id}`}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                padding: "10px 12px", border: "none", borderBottom: "1px solid var(--hairline-soft)",
                background: targetId === p.id ? "rgba(78,205,196,0.12)" : "transparent",
                cursor: "pointer", color: "var(--ink)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.name}</div>
                <div className="text-dim" style={{ fontSize: 11.5 }}>
                  {[p.role, p.relationship, p.location].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              {targetId === p.id && <Check size={14} style={{ color: "var(--ph-product-teal)" }} />}
            </button>
          ))
        )}
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="btn btn-ghost" onClick={onClose} data-testid={`merge-cancel-${person.id}`}>Cancel</button>
        <button
          className="btn btn-primary"
          onClick={merge}
          disabled={!targetId || merging}
          data-testid={`merge-confirm-${person.id}`}
        >
          {merging ? <Loader2 size={14} className="spin" /> : <UsersRound size={14} />} Merge {person.name.split(" ")[0]} → {target ? target.name.split(" ")[0] : "…"}
        </button>
      </div>
    </DialogShell>
  );
}

// ---------- Delete dialog ----------
function DeleteDialog({ person, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/people/${person.id}`);
      toast.success(`Deleted ${person.name}.`);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    } finally {
      setDeleting(false);
    }
  };
  return (
    <DialogShell title={`Delete ${person.name}?`} onClose={onClose} testid={`delete-dialog-${person.id}`}>
      <div style={{ fontSize: 13.5, color: "var(--ink-soft)", marginBottom: 14, lineHeight: 1.6 }}>
        This removes <strong>{person.name}</strong> from your People list AND deletes their
        Markdown profile in the Vault. Their pending profile suggestions are also dropped.
        <br /><br />
        Meeting attendee lists stay accurate &mdash; only the personal profile is removed.
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="btn btn-ghost" onClick={onClose} data-testid={`delete-cancel-${person.id}`}>Cancel</button>
        <button
          className="btn"
          onClick={doDelete}
          disabled={deleting}
          data-testid={`delete-confirm-${person.id}`}
          style={{ background: "var(--danger)", color: "white", borderColor: "transparent" }}
        >
          {deleting ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Delete person + vault note
        </button>
      </div>
    </DialogShell>
  );
}

// Minimal modal shell with backdrop + dismiss.
function DialogShell({ title, onClose, testid, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      data-testid={testid}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 80, background: "rgba(15, 23, 42, 0.45)",
        backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, animation: "fade-in 160ms ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 540, background: "var(--panel)", border: "1px solid var(--hairline)",
          borderRadius: 14, padding: "20px 22px", boxShadow: "0 24px 60px -16px rgba(15,23,42,0.5)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--ink-dim)", cursor: "pointer", padding: 4 }}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="profile-field">
      <div className="profile-field-label">{label}</div>
      {children}
      {hint && <div className="profile-field-hint">{hint}</div>}
    </div>
  );
}

function GroupsInput({ value, onChange, pid }) {
  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    api.get("/people/groups").then((r) => setSuggestions(r.data.groups || [])).catch(() => {});
  }, []);

  const add = (raw) => {
    const g = (raw || "").trim();
    if (!g) return;
    if (!value.some((x) => x.toLowerCase() === g.toLowerCase())) onChange([...value, g]);
    setDraft("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const available = suggestions.filter((s) => !value.some((x) => x.toLowerCase() === s.toLowerCase()));

  return (
    <div>
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
        padding: "8px 10px", borderRadius: 10, background: "var(--panel)", border: "1px solid var(--hairline)",
      }}>
        {value.map((g) => (
          <span key={g} data-testid={`group-tag-${pid}-${g.replace(/\s+/g, "-").toLowerCase()}`} style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999,
            background: "rgba(123,111,232,0.12)", border: "1px solid rgba(123,111,232,0.40)",
            color: "var(--ph-accent-purple)", fontSize: 12, fontWeight: 600,
          }}>
            {g}
            <X size={12} style={{ cursor: "pointer" }} onClick={() => onChange(value.filter((x) => x !== g))} />
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => add(draft)}
          placeholder={value.length ? "Add another…" : "e.g. Family, Executive Leadership Team…"}
          data-testid={`profile-groups-input-${pid}`}
          style={{ flex: 1, minWidth: 160, background: "transparent", border: "none", outline: "none", color: "var(--ink)", fontSize: 13 }}
        />
      </div>
      {available.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {available.slice(0, 8).map((s) => (
            <button key={s} onClick={() => add(s)} data-testid={`group-suggest-${pid}-${s.replace(/\s+/g, "-").toLowerCase()}`} style={{
              padding: "3px 10px", borderRadius: 999, fontSize: 11.5, cursor: "pointer",
              background: "var(--panel)", border: "1px dashed var(--hairline)", color: "var(--ink-dim)",
            }}>+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}
